import { useRef, useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import MessageModal from '@/components/ui/MessageModal';
import Axios from '@/shared/api/axios';
import { uploadVideoToCF } from '@/shared/utils/uploadVideoToCF';

const VALID_IMAGE_TYPES = ['image/jpg', 'image/jpeg', 'image/png', 'image/gif', 'image/webp'];

const THUMB_SIZE = 100;
const THUMB_GAP = 8;
const SCROLL_STEP = THUMB_SIZE + THUMB_GAP;

const VideoThumb = ({ src }) => {
  const { t } = useTranslation();
  const [unsupported, setUnsupported] = useState(false);
  return (
    <div className="position-relative rounded overflow-hidden bg-dark" style={{ width: `${THUMB_SIZE}px`, height: `${THUMB_SIZE}px` }}>
      <video
        src={src}
        muted
        playsInline
        preload="metadata"
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        onError={(e) => {
          if (e.target.error?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
            setUnsupported(true);
          }
        }}
      />
      {unsupported && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ pointerEvents: 'none' }}
        >
          <span className="text-white text-center px-1 fs-10" >
            {t('video_preview_unsupported')}
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * 附件按鈕 + 本機預覽（支援多個媒體，超出寬度時顯示左右箭頭導覽）
 * 影片選取後立即顯示 loading card，背景上傳至 Cloudflare Stream
 * 暴露 .open() 方法，讓外部 icon 觸發檔案選擇
 *
 * @param {Array}    props.previewFiles  [{ file?, mediaType, previewUrl?, uploading?, uid?, localId? }]
 * @param {Function} props.onFileSelect  (file, mediaType, previewUrl, localId?) => void
 * @param {Function} props.onFileRemove  (index) => void
 * @param {Function} props.onFileUpdate  (localId, updates) => void
 * @param {Function} props.onUploadFailed (localId) => void
 * @param {number}   props.idolId
 */
const ChatMediaAttachment = forwardRef(({ previewFiles, onFileSelect, onFileRemove, onFileUpdate, onUploadFailed, idolId }, ref) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const modalSizeRef = useRef(null);
  const modalTypeRef = useRef(null);
  const modalUploadErrorRef = useRef(null);
  const [fileSizeMsg, setFileSizeMsg] = useState('');
  const [fileTypeMsg, setFileTypeMsg] = useState('');
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useImperativeHandle(ref, () => ({
    open: () => inputRef.current?.click(),
  }));

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    requestAnimationFrame(updateArrows);
  }, [previewFiles]);

  const startVideoUpload = useCallback(async (file, localId, videoUrl) => {
    try {
      const { uid } = await uploadVideoToCF(
        file,
        () => Axios.post('/chat/get-stream-upload-url', { sender_idol_id: idolId }),
      );
      onFileUpdate(localId, { uid, previewUrl: null, videoUrl, uploading: false });
    } catch (error) {
      console.error('CF video upload failed:', error);
      modalUploadErrorRef.current?.show();
      onUploadFailed(localId);
    }
  }, [idolId, onFileUpdate, onUploadFailed]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';

    for (const file of files) {
      const isImage = VALID_IMAGE_TYPES.includes(file.type);
      const isVideo = file.type.startsWith('video/');

      if (!isImage && !isVideo) {
        setFileTypeMsg(t('image_type_error'));
        modalTypeRef.current?.show();
        continue;
      }

      const maxSize = isVideo ? 200 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        setFileSizeMsg(t(isVideo ? 'video_size_error' : 'image_size_error'));
        modalSizeRef.current?.show();
        continue;
      }

      if (isVideo) {
        const localId = `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        const videoUrl = URL.createObjectURL(file);
        onFileSelect(null, 'video', null, localId);
        startVideoUpload(file, localId, videoUrl);
      } else {
        const previewUrl = URL.createObjectURL(file);
        onFileSelect(file, 'image', previewUrl);
      }
    }
  };

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -SCROLL_STEP, behavior: 'smooth' });
  };

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: SCROLL_STEP, behavior: 'smooth' });
  };

  const total = previewFiles.length;

  return (
    <>
      {total > 0 && (
        <div className="position-relative px-3 pt-2 pb-1">
          <div
            ref={scrollRef}
            className="d-flex gap-2"
            style={{ overflowX: 'hidden' }}
            onScroll={updateArrows}
          >
            {previewFiles.map((item, index) => (
              <div key={index} className="position-relative flex-shrink-0">
                {item.uploading ? (
                  <div
                    className="bg-dark d-flex flex-column align-items-center justify-content-center rounded"
                    style={{ width: `${THUMB_SIZE}px`, height: `${THUMB_SIZE}px` }}
                  >
                    <div className="spinner-border spinner-border-sm text-white mb-1" role="status" />
                    <span className="text-white fs-10" >{t('video_uploading')}</span>
                  </div>
                ) : item.previewUrl ? (
                  <img
                    src={item.previewUrl}
                    alt="preview"
                    className="rounded"
                    style={{ width: `${THUMB_SIZE}px`, height: `${THUMB_SIZE}px`, objectFit: 'cover', objectPosition: 'center', display: 'block' }}
                  />
                ) : item.mediaType === 'video' && item.videoUrl ? (
                  <VideoThumb src={item.videoUrl} />
                ) : (
                  <div
                    className="bg-neutral rounded"
                    style={{ width: `${THUMB_SIZE}px`, height: `${THUMB_SIZE}px` }}
                  />
                )}

                {/* 左上計數器 */}
                <div
                  className="position-absolute bg-dark text-white px-2 py-1 rounded"
                  style={{ top: '6px', left: '6px', fontSize: '11px', zIndex: 10 }}
                >
                  {index + 1}/{total}
                </div>

                {/* 右上刪除按鈕 */}
                <FontAwesomeIcon
                  icon="fa-solid fa-circle-xmark"
                  className="position-absolute fs-4 bg-white rounded-pill cursor-p"
                  style={{ top: '4px', right: '4px', zIndex: 10 }}
                  onClick={() => onFileRemove(index)}
                />
              </div>
            ))}
          </div>

          {canScrollLeft && (
            <button
              type="button"
              className="btn btn-dark rounded-circle p-0 d-flex align-items-center justify-content-center position-absolute"
              style={{ width: '32px', height: '32px', top: '50%', left: '5px', transform: 'translateY(-50%)', zIndex: 10 }}
              onClick={scrollLeft}
            >
              <FontAwesomeIcon icon={faChevronLeft} className="fs-12" />
            </button>
          )}

          {canScrollRight && (
            <button
              type="button"
              className="btn btn-dark rounded-circle p-0 d-flex align-items-center justify-content-center position-absolute"
              style={{ width: '32px', height: '32px', top: '50%', right: '5px', transform: 'translateY(-50%)', zIndex: 10 }}
              onClick={scrollRight}
            >
              <FontAwesomeIcon icon={faChevronRight} className="fs-12" />
            </button>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={[...VALID_IMAGE_TYPES, 'video/*'].join(',')}
        onChange={handleFileChange}
        multiple
        hidden
      />

      <MessageModal ref={modalSizeRef} status="error">
        <p>{fileSizeMsg}</p>
      </MessageModal>
      <MessageModal ref={modalTypeRef} status="error">
        <p>{fileTypeMsg}</p>
      </MessageModal>
      <MessageModal ref={modalUploadErrorRef} status="error">
        <p>{t('video_upload_failed')}</p>
      </MessageModal>
    </>
  );
});

ChatMediaAttachment.displayName = 'ChatMediaAttachment';

export default ChatMediaAttachment;
